import React from 'react';
import { FiCheckCircle, FiAlertCircle, FiInfo } from 'react-icons/fi';
import { Chart as ChartJS, RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend } from 'chart.js';
import { Radar } from 'react-chartjs-2';

import { Card, CardHeader, CardTitle, CardContent } from '../common/Card';


// 注册Chart.js组件
ChartJS.register(RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

const EvaluationResult = ({ evaluation, loading }) => {

  // 调试输出评估结果
  console.log('评估结果数据:', evaluation);

  // 解析评估结果
  const parseEvaluationResult = (evaluation) => {
    if (!evaluation || !evaluation.evaluation_result) return null;

    // 如果评估结果是字符串，尝试解析为JSON
    if (typeof evaluation.evaluation_result === 'string') {
      try {
        const parsedResult = JSON.parse(evaluation.evaluation_result);

        // 检查是否是错误结果
        if (parsedResult.error) {
          console.error('评估错误:', parsedResult.message);
          return {
            isError: true,
            message: parsedResult.message,
            rawContent: parsedResult.raw_content
          };
        }

        return parsedResult;
      } catch (error) {
        console.error('解析评估结果失败:', error);
        return {
          isError: true,
          message: '解析评估结果失败',
          rawContent: evaluation.evaluation_result
        };
      }
    }

    // 如果已经是对象，检查是否是错误结果
    if (evaluation.evaluation_result.error) {
      return {
        isError: true,
        message: evaluation.evaluation_result.message,
        rawContent: evaluation.evaluation_result.raw_content
      };
    }

    return evaluation.evaluation_result;
  };

  // 解析评估结果
  const evaluationData = evaluation ? parseEvaluationResult(evaluation) : null;

  if (loading) {
    return (
      <Card className="h-full flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="text-muted-foreground">加载评估结果中...</p>
        </div>
      </Card>
    );
  }

  if (!evaluation) {
    return (
      <Card className="h-full flex items-center justify-center">
        <div className="text-center p-8 text-muted-foreground">
          请从左侧选择一个评估记录查看详情
        </div>
      </Card>
    );
  }



  return (
    <>
      <Card className="h-full flex flex-col overflow-hidden">
        <CardHeader className="flex flex-row items-center justify-between py-2 px-4">
          <div className="flex flex-col">
            <CardTitle className="text-xl">
              {evaluation.jd_title} - {evaluation.candidate_name}
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              评估时间: {new Date(evaluation.created_at).toLocaleString()}
            </p>
          </div>
        </CardHeader>
        <CardContent className="flex-1 overflow-hidden pt-2 px-4">
          <div className="flex flex-col gap-4 h-full overflow-hidden">

            <div>
              <h3 className="text-sm font-medium text-muted-foreground mb-1">评估结果</h3>
              <div className="rounded-md border bg-card h-[calc(100vh-190px)] overflow-y-auto">
                <div className="evaluation-result p-3 pb-6">
                  {evaluationData && evaluationData.isError ? (
                    // 错误模式: 显示错误信息
                    <div className="bg-red-50 p-4 mb-4 rounded-lg">
                      <p className="text-red-600 mb-2">错误: {evaluationData.message}</p>
                      {evaluationData.rawContent && (
                        <div className="mt-4 p-3 bg-gray-100 rounded overflow-auto max-h-[300px]">
                          <pre className="text-xs">{evaluationData.rawContent}</pre>
                        </div>
                      )}
                    </div>
                  ) : (
                    // JSON模式: 渲染结构化数据
                    <>
                      {/* 决策建议部分 */}
                      <h2 className="text-xl font-bold mb-4 pb-2 border-b">决策建议</h2>
                      <DecisionCard evaluationData={evaluationData} />

                      {/* 匹配度雷达图部分 */}
                      <h2 className="text-xl font-bold mt-8 mb-4 pb-2 border-b">匹配度雷达图</h2>
                      <RadarChartSection evaluationData={evaluationData} />

                      {/* 核心评估摘要部分 */}
                      <h2 className="text-xl font-bold mt-8 mb-4 pb-2 border-b">核心评估摘要</h2>
                      <CoreAssessment evaluationData={evaluationData} />

                      {/* 面试重点问题部分 */}
                      <h2 className="text-xl font-bold mt-8 mb-4 pb-2 border-b">面试重点问题建议</h2>
                      <InterviewQuestions evaluationData={evaluationData} />

                      {/* JD与简历匹配表部分 */}
                      <h2 className="text-xl font-bold mt-8 mb-4 pb-2 border-b">JD与简历匹配表</h2>
                      <MatchTable evaluationData={evaluationData} />
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>


    </>
  );
};

// 决策建议卡片组件
const DecisionCard = ({ evaluationData }) => {
  if (!evaluationData) return <div className="p-4 text-yellow-500">无评估数据</div>;

  // 检查数据格式是否正确
  if (!evaluationData.decision || !evaluationData.dimensions) {
    console.error('评估数据格式不正确:', evaluationData);
    return <div className="p-4 text-red-500">评估数据格式不正确</div>;
  }

  const { decision, overall_match_percentage, dimensions, priority_level, priority_suggestion } = evaluationData;

  // 决策类型对应的样式和图标
  const decisionStyles = {
    recommend: { borderColor: '#10b981', icon: <FiCheckCircle className="text-green-500" /> },
    further_assessment: { borderColor: '#f59e0b', icon: <FiInfo className="text-yellow-500" /> },
    not_recommend: { borderColor: '#ef4444', icon: <FiAlertCircle className="text-red-500" /> }
  };

  const style = decisionStyles[decision.type] || decisionStyles.further_assessment;

  return (
    <div className="card p-4 rounded-lg shadow-sm" style={{ borderLeft: `6px solid ${style.borderColor}` }}>
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold mb-2 flex items-center gap-2">
            {decision.title} {style.icon}
          </h2>
          <p className="text-gray-600">{decision.summary}</p>
        </div>
        <div className="text-center">
          <div className="text-5xl font-bold text-blue-600">{overall_match_percentage}%</div>
          <div className="text-gray-500">整体匹配度</div>
        </div>
      </div>

      <div className="mt-6">
        <h3 className="font-semibold mb-2">维度评分:</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {dimensions && dimensions.map((dim, index) => (
            <div key={index}>
              <div className="flex justify-between mb-1">
                <span>{dim.name}</span>
                <span>{dim.score}/10</span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full">
                <div
                  className="h-2 bg-blue-500 rounded-full"
                  style={{ width: `${dim.score * 10}%` }}
                ></div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-6 p-4 bg-blue-50 rounded-lg">
        <h3 className="font-semibold mb-2">优先级评级: <span className="text-blue-600">{priority_level}</span></h3>
        <p>{priority_suggestion}</p>
      </div>
    </div>
  );
};

// 雷达图部分组件
const RadarChartSection = ({ evaluationData }) => {
  if (!evaluationData) return <div className="p-4 text-yellow-500">无评估数据</div>;

  // 检查数据格式是否正确
  if (!evaluationData.dimensions || !Array.isArray(evaluationData.dimensions)) {
    console.error('维度数据格式不正确:', evaluationData);
    return <div className="p-4 text-red-500">维度数据格式不正确</div>;
  }

  const { dimensions, strongest_matches, notable_gaps } = evaluationData;

  const chartData = {
    labels: dimensions.map(dim => dim.name),
    datasets: [
      {
        label: '候选人水平',
        data: dimensions.map(dim => dim.score),
        backgroundColor: 'rgba(59, 130, 246, 0.2)',
        borderColor: 'rgba(59, 130, 246, 1)',
        pointBackgroundColor: 'rgba(59, 130, 246, 1)',
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: 'rgba(59, 130, 246, 1)'
      },
      {
        label: 'JD要求',
        data: dimensions.map(dim => dim.required_score),
        backgroundColor: 'rgba(239, 68, 68, 0.2)',
        borderColor: 'rgba(239, 68, 68, 1)',
        pointBackgroundColor: 'rgba(239, 68, 68, 1)',
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: 'rgba(239, 68, 68, 1)'
      }
    ]
  };

  const chartOptions = {
    scales: {
      r: {
        angleLines: { display: true },
        suggestedMin: 0,
        suggestedMax: 10
      }
    },
    maintainAspectRatio: false
  };

  return (
    <div className="card p-4 rounded-lg shadow-sm">
      <div style={{ height: '400px' }}>
        <Radar data={chartData} options={chartOptions} />
      </div>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="p-4 bg-gray-50 rounded-lg">
          <h3 className="font-semibold mb-2">最强匹配点</h3>
          <ul className="list-disc list-inside">
            {strongest_matches && strongest_matches.map((item, index) => (
              <li key={index}>{item.text} ({item.score}/10) - {item.detail}</li>
            ))}
          </ul>
        </div>
        <div className="p-4 bg-gray-50 rounded-lg">
          <h3 className="font-semibold mb-2">需注意差距</h3>
          <ul className="list-disc list-inside">
            {notable_gaps && notable_gaps.map((item, index) => (
              <li key={index}>{item.text} ({item.score}/10) - {item.detail}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

// 核心评估摘要组件
const CoreAssessment = ({ evaluationData }) => {
  if (!evaluationData) return <div className="p-4 text-yellow-500">无评估数据</div>;

  // 检查数据格式是否正确
  if (!evaluationData.strengths || !evaluationData.gaps || !evaluationData.risks) {
    console.error('评估摘要数据格式不正确:', evaluationData);
    return <div className="p-4 text-red-500">评估摘要数据格式不正确</div>;
  }

  const { strengths, gaps, risks, overall_assessment } = evaluationData;

  return (
    <div className="card p-4 rounded-lg shadow-sm">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="p-4 bg-green-50 rounded-lg">
          <h3 className="font-semibold mb-3 text-green-600"><FiCheckCircle className="inline mr-2" /> 突出优势</h3>
          <ul className="space-y-2">
            {strengths && strengths.map((item, index) => (
              <li key={index}>
                <strong>{item.title}:</strong> {item.description}
              </li>
            ))}
          </ul>
        </div>

        <div className="p-4 bg-yellow-50 rounded-lg">
          <h3 className="font-semibold mb-3 text-yellow-600"><FiInfo className="inline mr-2" /> 潜在差距</h3>
          <ul className="space-y-2">
            {gaps && gaps.map((item, index) => (
              <li key={index}>
                <strong>{item.title}:</strong> {item.description}
              </li>
            ))}
          </ul>
        </div>

        <div className="p-4 bg-red-50 rounded-lg">
          <h3 className="font-semibold mb-3 text-red-600"><FiAlertCircle className="inline mr-2" /> 风险信号</h3>
          <ul className="space-y-2">
            {risks && risks.map((item, index) => (
              <li key={index}>
                <strong>{item.title}:</strong> {item.description}
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="p-4 bg-blue-50 rounded-lg">
        <h3 className="font-semibold mb-3"><FiInfo className="inline mr-2 text-blue-500" /> 综合评估</h3>
        <p>{overall_assessment}</p>
      </div>
    </div>
  );
};

// 面试重点问题组件
const InterviewQuestions = ({ evaluationData }) => {
  if (!evaluationData) return <div className="p-4 text-yellow-500">无评估数据</div>;

  // 检查数据格式是否正确
  if (!evaluationData.interview_questions || !Array.isArray(evaluationData.interview_questions)) {
    console.error('面试问题数据格式不正确:', evaluationData);
    return <div className="p-4 text-red-500">面试问题数据格式不正确</div>;
  }

  const { interview_questions } = evaluationData;

  // 根据重要性设置背景颜色
  const getImportanceClass = (importance) => {
    switch (importance) {
      case 'high': return 'bg-red-50';
      case 'medium': return 'bg-yellow-50';
      case 'low': return 'bg-gray-50';
      default: return 'bg-gray-50';
    }
  };

  return (
    <div className="card p-4 rounded-lg shadow-sm">
      <div className="space-y-4">
        {interview_questions.map((q, index) => (
          <div key={index} className={`p-4 ${getImportanceClass(q.importance)} rounded-lg`}>
            <h3 className="font-semibold flex items-center">
              <span className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center mr-2">{index + 1}</span>
              {q.title}
            </h3>
            <p className="mt-2">{q.question}</p>
            <div className="mt-2 text-sm text-gray-500"><FiInfo className="inline mr-1" /> {q.purpose}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

// JD与简历匹配表组件
const MatchTable = ({ evaluationData }) => {
  if (!evaluationData) return <div className="p-4 text-yellow-500">无评估数据</div>;

  // 检查数据格式是否正确
  if (!evaluationData.match_table || !Array.isArray(evaluationData.match_table) || !evaluationData.match_statistics) {
    console.error('匹配表数据格式不正确:', evaluationData);
    return <div className="p-4 text-red-500">匹配表数据格式不正确</div>;
  }

  const { match_table, match_statistics } = evaluationData;

  // 匹配状态对应的样式
  const getMatchStatusClass = (status) => {
    switch (status) {
      case 'exceed': return 'bg-green-100 text-green-800';
      case 'full_match': return 'bg-blue-100 text-blue-800';
      case 'partial_match': return 'bg-yellow-100 text-yellow-800';
      case 'no_match': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // 匹配状态对应的文本
  const getMatchStatusText = (status) => {
    switch (status) {
      case 'exceed': return '超出要求';
      case 'full_match': return '完全匹配';
      case 'partial_match': return '部分匹配';
      case 'no_match': return '不匹配';
      default: return '未知';
    }
  };

  return (
    <div className="card p-4 rounded-lg shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-50">
              <th className="text-left p-3 border-b">JD要求</th>
              <th className="text-left p-3 border-b">候选人情况</th>
              <th className="text-center p-3 border-b">匹配状态</th>
            </tr>
          </thead>
          <tbody>
            {match_table.map((row, index) => (
              <tr key={index} className="border-b">
                <td className="p-3">{row.requirement}</td>
                <td className="p-3">{row.candidate_status}</td>
                <td className="p-3 text-center">
                  <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${getMatchStatusClass(row.match_status)}`}>
                    {getMatchStatusText(row.match_status)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 mb-4">
        <h3 className="font-semibold mb-2 text-sm">匹配度统计</h3>
        <div className="grid grid-cols-4 gap-2 text-center">
          <div className="p-2 bg-green-50 rounded-lg">
            <div className="text-lg font-bold text-green-600">{match_statistics.exceed}</div>
            <div className="text-sm">超出要求</div>
          </div>
          <div className="p-2 bg-blue-50 rounded-lg">
            <div className="text-lg font-bold text-blue-600">{match_statistics.full_match}</div>
            <div className="text-sm">完全匹配</div>
          </div>
          <div className="p-2 bg-yellow-50 rounded-lg">
            <div className="text-lg font-bold text-yellow-600">{match_statistics.partial_match}</div>
            <div className="text-sm">部分匹配</div>
          </div>
          <div className="p-2 bg-red-50 rounded-lg">
            <div className="text-lg font-bold text-red-600">{match_statistics.no_match}</div>
            <div className="text-sm">不匹配</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EvaluationResult;
